import React, { Suspense, useEffect } from "react";
import { Switch, Route, useLocation, Router } from "wouter";
import { RouteTransitionProvider, useTransitionLocation } from "@/contexts/RouteTransitionContext";
import TopProgressBar from "@/components/common/TopProgressBar";
import { QueryClientProvider } from "@tanstack/react-query";
import { publicQueryClient } from "@/lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider as UIThemeProvider } from "@/components/ui/theme-provider";
import { AuthProvider } from "@/contexts/AuthContext";
import { AppSettingsProvider } from "@/contexts/AppSettingsContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { FeaturesProvider } from "@/contexts/FeaturesContext";
import { SEOProvider } from "@/contexts/SEOContext";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import ConsentAwareAnalytics from "@/components/cookie/ConsentAwareAnalytics";
import ConsentAwareCloudflareAnalytics from "@/components/cookie/ConsentAwareCloudflareAnalytics";
import ConsentAwarePerformanceMonitor from "@/components/cookie/ConsentAwarePerformanceMonitor";
import ConsentAwareMarketingScripts from "@/components/cookie/ConsentAwareMarketingScripts";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import SEOMetaTags from "@/components/common/SEOMetaTags";
import ResourcePreloader from "@/components/common/ResourcePreloader";
import { usePerformanceOptimization } from "@/hooks/usePerformanceOptimization";
import { DEFAULTS } from '@webready/shared/config';
import { useLangAttribute } from "@/hooks/useLangAttribute";
import { useRoutePrefetch } from "@/hooks/useRoutePrefetch";
import { useAppLanguage } from "@/hooks/useAppLanguage";
import PublicLoadingFallback from "@/components/common/PublicLoadingFallback";
import PublicLayoutShell from "@/components/common/PublicLayoutShell";
import { lazyWithPreload } from "@/utils/lazyWithPreload";
import { useBrandingSettings } from "@/contexts/AppSettingsContext";
import LegalDocuments from "@/components/legal/LegalDocuments";

// Lazy imports for prefetch and auth routes
const Services = lazyWithPreload(() => import("@/pages/Services"));
const ServiceDetails = lazyWithPreload(() => import("@/pages/ServiceDetails"));
const ServiceDetailsRouteWrapper = lazyWithPreload(() => import("@/components/services/ServiceDetailsRouteWrapper"));
const Team = lazyWithPreload(() => import("@/pages/Team"));
const Gallery = lazyWithPreload(() => import("@/pages/Gallery"));
const Contact = lazyWithPreload(() => import("@/pages/Contact"));
const Blog = lazyWithPreload(() => import("@/pages/Blog"));
const LoginPage = lazyWithPreload(() => import("@/pages/Login"));
const Register = lazyWithPreload(() => import("@/pages/Register"));
const BlogPostPage = lazyWithPreload(() => import("@/pages/BlogPostPage"));
const Testimonials = lazyWithPreload(() => import("@/pages/Testimonials"));

// Cookie Consent component - Lazy loaded
const CookieConsent = lazyWithPreload(() => import('@/components/cookie/CookieConsent'));

// Component pentru sincronizarea limbii - trebuie să fie în interiorul QueryClientProvider
function LanguageSync() {
  useAppLanguage(); // Sincronizează limba cu setările din baza de date
  return null;
}

// Component to normalize trailing slashes client-side
function TrailingSlashNormalizer() {
  const [location, setLocation] = useLocation();
  useEffect(() => {
    if (location !== '/' && location.endsWith('/')) {
      setLocation(location.slice(0, -1), { replace: true });
    }
  }, [location, setLocation]);
  return null;
}

function PublicRouter() {
  return (
    <Switch>
      <Route path="/login" component={LoginPage} />
      <Route path="/register" component={Register} />

      {/* Dashboard routes - redirect to dashboard entry */}
      {/* This route only handles dashboard routes accessed from public entry point */}
      <Route path="/dashboard/:rest*">
        {() => {
          // Redirect to dashboard entry point
          // This only happens when someone tries to access /dashboard/* from public entry
          if (typeof window !== 'undefined') {
            const currentPath = window.location.pathname + window.location.search;
            // Check if we're already in dashboard entry (shouldn't happen, but safety check)
            if (window.location.pathname.includes('dashboard-entry.html')) {
              return null;
            }
            // Use full page reload to dashboard entry with hash routing
            // The dashboard-entry.tsx will convert hash to browser routing
            window.location.replace(`/dashboard-entry.html#${currentPath}`);
          }
          return null;
        }}
      </Route>
      
      {/* Public site: persistent MainLayout (video, navbar, footer) */}
      <Route component={PublicLayoutShell} />
    </Switch>
  );
}

const getOrigin = (urlStr?: string): string | null => {
  if (!urlStr) return null;
  try {
    const trimmed = urlStr.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return new URL(trimmed).origin;
    }
  } catch {
    // ignore
  }
  return null;
};

// Component for SEO Meta Tags that uses branding settings
function SEOMetaTagsWithBranding() {
  const { appName } = useBrandingSettings();
  const supabaseUrl = getOrigin(import.meta.env.VITE_SUPABASE_URL);
  const apiUrl = getOrigin(import.meta.env.VITE_API_BASE_URL);

  const preconnectList = [
    supabaseUrl ? { href: supabaseUrl, crossOrigin: false as const } : null,
    apiUrl ? { href: apiUrl, crossOrigin: 'anonymous' as const } : null,
    'https://fonts.googleapis.com',
    { href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' as const }
  ].filter(Boolean) as any[];

  return (
    <SEOMetaTags
      themeColor={DEFAULTS.themeColor}
      appleMobileWebAppTitle={appName || undefined}
      preconnect={preconnectList}
      dnsPrefetch={[
        'https://www.google-analytics.com',
        'https://www.googletagmanager.com',
        'https://challenges.cloudflare.com'
      ]}
    />
  );
}

function PublicApp() {
  useLangAttribute(); // Set lang attribute dinamic
  
  // Initialize performance optimizations
  usePerformanceOptimization({
    enablePreload: true,
    enablePrefetch: true,
    enableResourceHints: true,
    enableImageOptimization: true,
  });

  // Prefetch critical public routes on link hover (ONLY public routes)
  useRoutePrefetch({
    routes: [
      { path: '/services', component: Services },
      { path: '/blog', component: Blog },
      { path: '/team', component: Team },
      { path: '/gallery', component: Gallery },
      { path: '/contact', component: Contact },
      { path: '/testimonials', component: Testimonials },
    ],
    delay: 100,
    prefetchOnLoad: false, // Only prefetch on hover/focus, not on load
  });

  // Contextual prefetch: when on /blog, prefetch blog post pages on hover
  const [location] = useLocation();
  useEffect(() => {
    if (location === '/blog') {
      // Prefetch blog post pages when hovering over blog post links
      const blogLinks = document.querySelectorAll('a[href^="/blog/"]');
      const handleBlogHover = (e: Event) => {
        const link = e.currentTarget as HTMLAnchorElement;
        const href = link.getAttribute('href');
        if (href && href.startsWith('/blog/') && href !== '/blog') {
          BlogPostPage.preload?.();
        }
      };
      
      blogLinks.forEach((link) => {
        link.addEventListener('mouseenter', handleBlogHover, { once: true });
      });

      return () => {
        blogLinks.forEach((link) => {
          link.removeEventListener('mouseenter', handleBlogHover);
        });
      };
    }

    if (location === '/services') {
      // Prefetch service detail pages when hovering over service links
      const serviceLinks = document.querySelectorAll('a[href^="/service/"], a[href^="/services/"]');
      const handleServiceHover = (e: Event) => {
        const link = e.currentTarget as HTMLAnchorElement;
        const href = link.getAttribute('href');
        if (href && (href.startsWith('/service/') || href.startsWith('/services/'))) {
          if (href.startsWith('/service/')) {
            ServiceDetails.preload?.();
          } else {
            ServiceDetailsRouteWrapper.preload?.();
          }
        }
      };
      
      serviceLinks.forEach((link) => {
        link.addEventListener('mouseenter', handleServiceHover, { once: true });
      });

      return () => {
        serviceLinks.forEach((link) => {
          link.removeEventListener('mouseenter', handleServiceHover);
        });
      };
    }
  }, [location]);
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={publicQueryClient}>
        <HelmetProvider>
          <UIThemeProvider defaultTheme="light">
            <ThemeProvider>
              <AuthProvider>
                <AppSettingsProvider>
                  <SubscriptionProvider>
                    <FeaturesProvider>
                      <CookieConsentProvider>
                        <SEOProvider>
                          <ConsentAwareAnalytics>
                            <ConsentAwareCloudflareAnalytics />
                            <TooltipProvider>
                              <LanguageSync />
                              <TrailingSlashNormalizer />
                              <RouteTransitionProvider>
                                <Router hook={useTransitionLocation}>
                                  <TopProgressBar />
                                  <Suspense fallback={<PublicLoadingFallback />}>
                                    <PublicRouter />
                                  </Suspense>
                                </Router>
                              </RouteTransitionProvider>
                              <Suspense fallback={null}>
                                <CookieConsent />
                              </Suspense>
                              <LegalDocuments />
                              <Toaster />
                              <ConsentAwarePerformanceMonitor />
                              <ConsentAwareMarketingScripts />
                              <SEOMetaTagsWithBranding />
                              <ResourcePreloader
                                prefetchResources={[
                                  // Only prefetch services on services pages
                                  ...(location === '/services' || location.startsWith('/service/') || location.startsWith('/services/') ? ['/api/services'] : []),
                                  // Only prefetch testimonials on testimonials page
                                  ...(location === '/testimonials' ? ['/api/testimonials'] : [])
                                  // Removed '/api/hero' - only needed on Home page, not on all pages
                                ]}
                              />
                            </TooltipProvider>
                          </ConsentAwareAnalytics>
                        </SEOProvider>
                      </CookieConsentProvider>
                    </FeaturesProvider>
                  </SubscriptionProvider>
                </AppSettingsProvider>
              </AuthProvider>
            </ThemeProvider>
          </UIThemeProvider>
        </HelmetProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default PublicApp;

